import React, { useMemo, useState } from "react";
import { Card, DropdownField, Text, View } from "../base";
import { Icon } from "../base/Icon";
import { VariantDistributionChart } from "./VariantDistributionChart";
import { OutcomeComparisonChart } from "./OutcomeComparisonChart";
import { StringOutcomeComparisonChart } from "./StringOutcomeComparisonChart";
import { VariantRankingChart } from "./VariantRankingChart";
import { TargetSelectionField } from "./TargetSelectionField";
import {
  processVariantDataForPieChart,
  processStringOutcomeData,
  processBooleanOutcomeData,
  processIntegerOutcomeData,
  processFloatOutcomeData,
  calculateStatisticalSignificance,
  rankVariants,
  ComparisonDataPoint,
  TargetConfig,
} from "../../utils/chartData";

interface TestAnalyticsDashboardProps {
  optionCounts: Record<string, { count: number; outcomes: Record<string, { counts: Record<string, number> }> }>;
  outcomes: Record<string, { type: "boolean" | "integer" | "float" | "string"; counts: Record<string, number> }>;
  sessionCount: number;
}

export const TestAnalyticsDashboard: React.FC<TestAnalyticsDashboardProps> = ({ optionCounts, outcomes, sessionCount }) => {
  const [selectedOutcome, setSelectedOutcome] = useState<string>("");
  const [selectedMetric, setSelectedMetric] = useState<string>("successRateValue");
  const [targetConfig, setTargetConfig] = useState<TargetConfig | null>({ type: "maximize" });

  // Process data for charts
  const variantData = useMemo(() => processVariantDataForPieChart(optionCounts), [optionCounts]);

  const availableOutcomes = useMemo(() => {
    return Object.keys(outcomes).map((outcome) => ({
      value: outcome,
      label: outcome,
    }));
  }, [outcomes]);

  const selectedOutcomeData = useMemo(() => {
    if (!selectedOutcome) return null;

    const outcomeType = outcomes[selectedOutcome]?.type;
    if (!outcomeType) return null;

    // Update selected metric based on outcome type
    switch (outcomeType) {
      case "boolean":
        setSelectedMetric("successRateValue");
        return processBooleanOutcomeData(optionCounts, selectedOutcome);
      case "integer":
        setSelectedMetric("averageValue");
        return processIntegerOutcomeData(optionCounts, selectedOutcome);
      case "float":
        setSelectedMetric("averageValue");
        return processFloatOutcomeData(optionCounts, selectedOutcome);
      case "string":
        setSelectedMetric("topValuePercentageValue");
        return processStringOutcomeData(optionCounts, selectedOutcome);
      default:
        setSelectedMetric("topValuePercentageValue");
        return processStringOutcomeData(optionCounts, selectedOutcome);
    }
  }, [optionCounts, outcomes, selectedOutcome]);

  const significanceData = useMemo<Record<string, { significance: string; confidence: number; pValue: number }>>(() => {
    if (!selectedOutcomeData) return {};
    return calculateStatisticalSignificance(selectedOutcomeData, selectedMetric);
  }, [selectedOutcomeData, selectedMetric]);

  const variantRankings = useMemo(() => {
    if (!selectedOutcomeData) return [];
    return rankVariants(selectedOutcomeData, selectedMetric, targetConfig || undefined);
  }, [selectedOutcomeData, selectedMetric, targetConfig]);

  const getMetricOptions = () => {
    if (!selectedOutcomeData || selectedOutcomeData.length === 0) return [];

    const outcomeType = outcomes[selectedOutcome]?.type;
    const options = [];

    switch (outcomeType) {
      case "boolean":
        options.push({ value: "successRateValue", label: "Success Rate (%)" });
        break;
      case "integer":
      case "float":
        options.push({ value: "averageValue", label: "Average Value" });
        break;
      case "string":
        options.push({ value: "topValuePercentageValue", label: "Top Value Rate (%)" });
        break;
    }

    return options;
  };

  const getYAxisLabel = () => {
    const outcomeType = outcomes[selectedOutcome]?.type;
    switch (outcomeType) {
      case "boolean":
        return "Success Rate (%)";
      case "integer":
      case "float":
        return "Average Value";
      case "string":
        return "Top Value Rate (%)";
      default:
        return "Value";
    }
  };

  const getChartTitle = () => {
    if (!selectedOutcome) return "Outcome Comparison";

    const outcomeType = outcomes[selectedOutcome]?.type;
    switch (outcomeType) {
      case "boolean":
        return `${selectedOutcome} - Success Rate Comparison`;
      case "integer":
        return `${selectedOutcome} - Integer Average Comparison`;
      case "float":
        return `${selectedOutcome} - Float Average Comparison`;
      case "string":
        return `${selectedOutcome} - Top Value Rate Comparison`;
      default:
        return `${selectedOutcome} - Comparison`;
    }
  };

  return (
    <View className="space-y-6 mb-8">
      {/* Variant Distribution */}
      <VariantDistributionChart
        data={variantData}
        title="Session Distribution by Variant"
      />

      {/* Outcome Selection */}
      <Card>
        <View className="p-6">
          <View className="flex items-center mb-4">
            <Icon
              name="target"
              size={20}
              className="mr-2 text-purple-600 dark:text-purple-400"
            />
            <Text className="text-lg font-semibold">Outcome Analysis</Text>
          </View>

          <View className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <View>
              <Text className="text-sm font-medium mb-2">Select Outcome</Text>
              <DropdownField
                value={selectedOutcome}
                onChange={setSelectedOutcome}
                options={availableOutcomes}
                placeholder="Choose an outcome to analyze"
                className="w-full"
              />
            </View>

            {selectedOutcomeData && getMetricOptions().length > 1 && (
              <View>
                <Text className="text-sm font-medium mb-2">Select Metric</Text>
                <DropdownField
                  value={selectedMetric}
                  onChange={setSelectedMetric}
                  options={getMetricOptions()}
                  placeholder="Choose a metric"
                  className="w-full"
                />
              </View>
            )}
          </View>

          {/* Target Configuration for Numerical Outcomes */}
          {selectedOutcomeData && ["integer", "float"].includes(outcomes[selectedOutcome]?.type || "") && (
            <View className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <TargetSelectionField
                value={targetConfig}
                onChange={setTargetConfig}
                disabled={false}
              />
            </View>
          )}
        </View>
      </Card>

      {/* Outcome Comparison Chart */}
      {selectedOutcomeData && (
        <>
          {outcomes[selectedOutcome]?.type === "string" ? (
            <StringOutcomeComparisonChart
              data={selectedOutcomeData}
              title={getChartTitle()}
              significanceData={significanceData}
            />
          ) : (
            <OutcomeComparisonChart
              data={selectedOutcomeData}
              title={getChartTitle()}
              metric={selectedMetric}
              yAxisLabel={getYAxisLabel()}
              significanceData={significanceData}
            />
          )}
        </>
      )}

      {/* Variant Rankings */}
      {variantRankings.length > 0 && (
        <VariantRankingChart
          rankings={variantRankings}
          title={`${selectedOutcome} - Variant Rankings`}
          metric={selectedMetric}
          targetConfig={targetConfig}
        />
      )}

      {/* Summary Statistics */}
      {selectedOutcomeData && (
        <Card>
          <View className="p-6">
            <View className="flex items-center mb-4">
              <Icon
                name="activity"
                size={20}
                className="mr-2 text-orange-600 dark:text-orange-400"
              />
              <Text className="text-lg font-semibold">Summary Statistics</Text>
            </View>

            <View className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedOutcomeData.map((variant, index) => {
                const uniqueKey =
                  outcomes[selectedOutcome]?.type === "string" && variant.stringValue ? `${variant.variant}-${variant.stringValue}` : variant.variant;
                return (
                  <View
                    key={uniqueKey}
                    className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <Text className="font-medium text-lg mb-2">{variant.variant}</Text>
                    <View className="space-y-1">
                      <View className="flex justify-between">
                        <Text className="text-sm text-gray-600 dark:text-gray-400">Sessions:</Text>
                        <Text className="text-sm font-medium">{variant.count}</Text>
                      </View>
                      {variant.totalOutcomes !== undefined && (
                        <View className="flex justify-between">
                          <Text className="text-sm text-gray-600 dark:text-gray-400">Outcomes:</Text>
                          <Text className="text-sm font-medium">{variant.totalOutcomes}</Text>
                        </View>
                      )}
                      {variant.successRateValue !== undefined && (
                        <View className="flex justify-between">
                          <Text className="text-sm text-gray-600 dark:text-gray-400">Success Rate:</Text>
                          <Text className="text-sm font-medium">{variant.successRate}%</Text>
                        </View>
                      )}
                      {variant.averageValue !== undefined && (
                        <View className="flex justify-between">
                          <Text className="text-sm text-gray-600 dark:text-gray-400">Average:</Text>
                          <Text className="text-sm font-medium">{variant.average}</Text>
                        </View>
                      )}
                      {variant.rateValue !== undefined && outcomes[selectedOutcome]?.type === "string" && (
                        <View className="flex justify-between">
                          <Text className="text-sm text-gray-600 dark:text-gray-400">Rate:</Text>
                          <Text className="text-sm font-medium">{variant.rate}%</Text>
                        </View>
                      )}
                      {variant.stringValue !== undefined && (
                        <View className="flex justify-between">
                          <Text className="text-sm text-gray-600 dark:text-gray-400">Value:</Text>
                          <Text className="text-sm font-medium truncate max-w-20">{variant.stringValue}</Text>
                        </View>
                      )}
                      {(() => {
                        const significanceKey =
                          outcomes[selectedOutcome]?.type === "string" && variant.stringValue ? `${variant.variant}-${variant.stringValue}` : variant.variant;
                        const significanceResult = significanceData[significanceKey];

                        return significanceResult ? (
                          <View className="flex justify-between">
                            <Text className="text-sm text-gray-600 dark:text-gray-400">Significance:</Text>
                            <View
                              className={`px-2 py-1 rounded text-xs ${
                                significanceResult.confidence >= 99
                                  ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                  : significanceResult.confidence >= 95
                                    ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                                    : significanceResult.confidence >= 90
                                      ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
                                      : significanceResult.confidence >= 80
                                        ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                                        : significanceResult.confidence >= 70
                                          ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                                          : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                              }`}
                            >
                              <Text className="text-xs">{significanceResult.significance}</Text>
                            </View>
                          </View>
                        ) : null;
                      })()}
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
        </Card>
      )}
    </View>
  );
};
